import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { ChevronLeft, ChevronRight, Plus, Sparkles, Video, FileText, Phone, Users, Clock, Calendar, CalendarCheck, Filter, Globe } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EVENT_TYPES = {
  demo: { color: "var(--purple)", label: "Demo" },
  sync: { color: "var(--blue)", label: "Sync" },
  contract: { color: "var(--green)", label: "Contract" },
  proposal: { color: "var(--amber)", label: "Proposal" },
  onboard: { color: "var(--teal)", label: "Onboarding" },
  urgent: { color: "var(--red)", label: "Urgent" },
  followup: { color: "var(--blue)", label: "Follow-up" },
  call: { color: "#0071e3", label: "Call" },
  internal: { color: "#8e8e93", label: "Internal" },
  demo_reschedule: { color: "var(--amber)", label: "Demo (rescheduled)" },
};

const FILTER_CHIPS = [
  { id: "all", label: "All events", color: "#1b365D" },
  { id: "demo", label: "Demos", color: "var(--purple)" },
  { id: "followup", label: "Follow-ups", color: "var(--blue)" },
  { id: "call", label: "Calls", color: "#0071e3" },
  { id: "internal", label: "Internal", color: "#8e8e93" },
  { id: "contract", label: "Contracts", color: "var(--green)" },
];

const TIMEZONES = [
  { id: "America/New_York", label: "Eastern (ET)" },
  { id: "America/Chicago", label: "Central (CT)" },
  { id: "America/Denver", label: "Mountain (MT)" },
  { id: "America/Los_Angeles", label: "Pacific (PT)" },
  { id: "UTC", label: "UTC" },
];

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:${String(m || 0).padStart(2, "0")} ${suffix}`;
};

export default function CalendarPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const _now = new Date();
  const [month, setMonth] = useState(_now.getMonth());
  const [year, setYear] = useState(_now.getFullYear());
  const [activeFilter, setActiveFilter] = useState("all");
  const [timezone, setTimezone] = useState("America/New_York");
  const [demoBookings, setDemoBookings] = useState([]);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [newEventDate, setNewEventDate] = useState(null); // {year, month, day} when invoked from a day cell
  const [selectedDay, setSelectedDay] = useState(null); // {year, month, day} — drives Day Events panel

  useEffect(() => {
    axios.get(`${API}/demo-bookings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDemoBookings(r.data || []))
      .catch(() => {});
  }, [token]);

  // Convert demo bookings to calendar events
  const demoEvents = demoBookings
    // Iter 259 (Jul 2026, Fix H) — hide canceled bookings from the
    // master calendar. When a rep cancels a meeting in Outlook, the
    // Graph webhook now flips demo_bookings.status → "canceled"; we
    // filter those out so the CRM calendar matches Outlook.
    .filter(d => d.status !== "canceled")
    .map(d => {
    // Iter 177.7 (Lisa #ca843c39 Feb 2026) — calendar showed meetings
    // on the WRONG day (Lisa scheduled for 6/12 in Outlook but the CRM
    // displayed 6/13). Root cause: the backend may return the booking
    // as either:
    //   • `d.date` = "2026-06-13" (UTC date) + `d.time` = "21:00"
    //     for a 9pm-EST/6/12 meeting → CRM blindly used the UTC date
    //   • `d.start_iso` = ISO datetime (TZ-aware) → preferred path
    // Fix: when an ISO datetime is present, derive the date + time in
    // the rep's LOCAL timezone (which is what Outlook shows). Falls
    // back to the date-only string for legacy rows.
    //
    // Iter 258 (Jul 2026, Fix F) — extended the ISO fallback chain to
    // also accept `d.scheduled_at`. Older mirror rows written before
    // Iter 258 lacked `start_iso` and forced the calendar into the
    // noon-fallback, producing "all meetings render at 12:00 PM".
    let dateObj = null;
    if (d.start_iso || d.start_at || d.start || d.scheduled_at) {
      try {
        dateObj = new Date(d.start_iso || d.start_at || d.start || d.scheduled_at);
        if (Number.isNaN(dateObj.getTime())) dateObj = null;
      } catch { dateObj = null; }
    }
    if (!dateObj && d.date) {
      // Date-only string from a legacy booking — append local-noon so
      // we don't accidentally land on the previous calendar day in
      // any negative-UTC timezone.
      dateObj = new Date(d.date + "T12:00:00");
    }
    const isCurrentMonth = dateObj && dateObj.getFullYear() === year && dateObj.getMonth() === month;
    // Prefer the time encoded in the ISO datetime (already in local
    // TZ after the Date constructor parses it) over the persisted
    // free-text `d.time` field which may be UTC.
    //
    // Iter 283 (Jul 2026) — when we fell through to the noon-fallback
    // (no ISO source), the previous code silently used the fallback
    // dateObj's toLocaleTimeString → every legacy row showed 12:00 PM.
    // Now: prefer the free-text `d.time` (e.g. "16:00") whenever no
    // ISO source was available. Real ISO source → dateObj wins;
    // free-text time → fmt12(d.time) wins; nothing → dateObj's noon
    // (worst case, matches pre-Iter 283 behavior for truly-empty rows).
    const hadIsoSource = !!(d.start_iso || d.start_at || d.start || d.scheduled_at);
    const localTimeFmt = hadIsoSource && dateObj
      ? dateObj.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : (d.time
          ? fmt12(d.time)
          : (dateObj ? dateObj.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""));
    return {
      id: `demo-${d.id}`,
      title: `Demo — ${d.company || d.lead_title || d.name || d.contact_name || "Unknown"}`,
      type: d.status === "rescheduled" ? "demo_reschedule" : "demo",
      day: isCurrentMonth ? dateObj.getDate() : -1,
      time: localTimeFmt,
      category: "demo",
      demoId: d.id,
      isDemo: true,
    };
  }).filter(e => e.day > 0);

  const allEvents = demoEvents;

  const filteredEvents = activeFilter === "all" ? allEvents : allEvents.filter(e => {
    if (activeFilter === "demo") return e.type === "demo" || e.type === "demo_reschedule";
    if (activeFilter === "followup") return e.type === "followup";
    if (activeFilter === "call") return e.type === "call";
    if (activeFilter === "internal") return e.category === "internal" || e.type === "sync" || e.type === "onboard";
    if (activeFilter === "contract") return e.type === "contract";
    return true;
  });

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const calendarDays = [];
  for (let i = firstDay - 1; i >= 0; i--) calendarDays.push({ day: prevDays - i, current: false });
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push({ day: i, current: true });
  const remaining = 42 - calendarDays.length;
  for (let i = 1; i <= remaining; i++) calendarDays.push({ day: i, current: false });

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const todayEvents = filteredEvents.filter(e => e.day === today.getDate() && isCurrentMonth);
  const weekStart = today.getDate() - today.getDay();
  const weekEvents = filteredEvents.filter(e => e.day >= weekStart && e.day <= weekStart + 6);

  const demoCount = allEvents.filter(e => e.type === "demo" || e.type === "demo_reschedule").length;
  const rescheduleCount = allEvents.filter(e => e.type === "demo_reschedule").length;

  return (
    <div data-testid="calendar-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Master Calendar</h1>
          <p className="page-subtitle">All events, demos, meetings, and deadlines in one view</p>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", background: "var(--gray-light)", borderRadius: 6 }}>
            <Globe style={{ width: 11, height: 11, color: "var(--text-tertiary)" }} />
            <select value={timezone} onChange={e => setTimezone(e.target.value)} data-testid="timezone-selector"
              style={{ border: "none", background: "transparent", fontSize: 11, fontFamily: "var(--font)", color: "var(--text-secondary)", cursor: "pointer", outline: "none" }}>
              {TIMEZONES.map(tz => <option key={tz.id} value={tz.id}>{tz.label}</option>)}
            </select>
          </div>
          <span className="tag tag-green" style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 8px" }}>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--green)" }} /> Microsoft 365 synced
          </span>
          <button className="btn btn-primary" onClick={() => setShowNewEvent(true)} data-testid="new-event-btn">
            <Plus style={{ width: 13, height: 13 }} /> New Event
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Filter Chips */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "wrap" }} data-testid="calendar-filter-chips">
          <Filter style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} />
          {FILTER_CHIPS.map(chip => (
            <button key={chip.id} onClick={() => setActiveFilter(chip.id)} data-testid={`cal-filter-${chip.id}`}
              style={{
                padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: activeFilter === chip.id ? 600 : 400,
                background: activeFilter === chip.id ? `${chip.color}15` : "var(--gray-light)",
                color: activeFilter === chip.id ? chip.color : "var(--text-tertiary)",
                border: activeFilter === chip.id ? `1px solid ${chip.color}40` : "1px solid transparent",
                cursor: "pointer", fontFamily: "var(--font)", transition: "all 0.15s",
              }}>
              {chip.id !== "all" && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: chip.color, marginRight: 4, verticalAlign: "middle" }} />}
              {chip.label}
            </button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 12 }}>
          {/* Calendar Grid */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "0.5px solid var(--border)" }}>
              <button className="btn" style={{ padding: "4px 8px" }} onClick={() => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); }}>
                <ChevronLeft style={{ width: 14, height: 14 }} />
              </button>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{monthNames[month]} {year}</span>
              <button className="btn" style={{ padding: "4px 8px" }} onClick={() => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); }}>
                <ChevronRight style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {DAYS.map(d => (
                <div key={d} style={{ padding: "8px", textAlign: "center", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", borderBottom: "0.5px solid var(--border)" }}>{d}</div>
              ))}
              {calendarDays.map((d, i) => {
                const isToday = isCurrentMonth && d.current && d.day === today.getDate();
                const dayEvents = d.current ? filteredEvents.filter(e => e.day === d.day) : [];
                const isSelected = selectedDay && d.current && selectedDay.year === year && selectedDay.month === month && selectedDay.day === d.day;
                return (
                  <div
                    key={i}
                    onClick={d.current ? () => setSelectedDay({ year, month, day: d.day }) : undefined}
                    data-testid={d.current ? `cal-day-${year}-${month + 1}-${d.day}` : undefined}
                    style={{
                      minHeight: 80, padding: 6, borderBottom: "0.5px solid var(--border)", borderRight: (i + 1) % 7 !== 0 ? "0.5px solid var(--border)" : "none",
                      background: isSelected ? "#e6f0ff" : isToday ? "var(--blue-light)" : !d.current ? "#fafaf9" : "var(--surface)",
                      opacity: d.current ? 1 : 0.5,
                      cursor: d.current ? "pointer" : "default",
                      outline: isSelected ? "2px solid var(--blue)" : "none",
                    }}>
                    <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? "var(--blue)" : "var(--text-primary)", marginBottom: 4,
                      ...(isToday ? { width: 22, height: 22, borderRadius: "50%", background: "var(--blue)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" } : {})
                    }}>{d.day}</div>
                    {dayEvents.slice(0, 2).map(ev => {
                      const et = EVENT_TYPES[ev.type] || EVENT_TYPES.sync;
                      return (
                        <div key={ev.id} onClick={ev.isDemo ? (e) => { e.stopPropagation(); navigate("/demo-bookings"); } : undefined}
                          style={{
                            fontSize: 9, fontWeight: 700, padding: "2px 4px", borderRadius: 4,
                            background: `${et.color}15`, color: et.color, marginBottom: 2,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            borderLeft: `2px solid ${et.color}`,
                            cursor: ev.isDemo ? "pointer" : "default",
                            ...(ev.type === "demo_reschedule" ? { borderStyle: "dashed" } : {}),
                          }}
                          title={ev.isDemo ? "Click to view in Demo Bookings" : ev.title}
                          data-testid={`cal-event-${ev.id}`}
                        >{ev.title.length > 18 ? ev.title.slice(0, 18) + "..." : ev.title}</div>
                      );
                    })}
                    {dayEvents.length > 2 && <div style={{ fontSize: 8, color: "var(--text-tertiary)" }}>+{dayEvents.length - 2} more</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {selectedDay && (() => {
              const sd = selectedDay;
              const dayLabel = `${monthNames[sd.month]} ${sd.day}, ${sd.year}`;
              const sameMonth = sd.year === year && sd.month === month;
              const evs = sameMonth ? filteredEvents.filter(e => e.day === sd.day) : [];
              return (
                <div className="card" data-testid="calendar-day-panel" style={{ borderLeft: "3px solid var(--blue)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div className="card-title" style={{ margin: 0 }}>{dayLabel}</div>
                    <button onClick={() => setSelectedDay(null)} data-testid="calendar-day-panel-close"
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#888", lineHeight: 1 }}>&times;</button>
                  </div>
                  {evs.length === 0
                    ? <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "4px 0 8px" }}>No events on this day.</p>
                    : evs.map(ev => {
                        const et = EVENT_TYPES[ev.type] || EVENT_TYPES.sync;
                        return (
                          <div key={ev.id} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "0.5px solid var(--border)", cursor: ev.isDemo ? "pointer" : "default" }}
                            onClick={ev.isDemo ? () => navigate("/demo-bookings") : undefined}>
                            <div style={{ width: 3, borderRadius: 2, background: et.color, flexShrink: 0 }} />
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600 }}>{ev.title}</div>
                              <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{ev.time || et.label}</div>
                            </div>
                          </div>
                        );
                      })}
                  <button className="btn btn-primary" data-testid="calendar-day-panel-new-event"
                    onClick={() => { setNewEventDate({ year: sd.year, month: sd.month, day: sd.day }); setShowNewEvent(true); }}
                    style={{ marginTop: 8, width: "100%", padding: "6px 10px", fontSize: 11 }}>
                    <Plus style={{ width: 12, height: 12 }} /> New event on this day
                  </button>
                </div>
              );
            })()}
            <div className="card">
              <div className="card-title" style={{ marginBottom: 8 }}>Today</div>
              {todayEvents.length === 0 ? <p style={{ fontSize: 12, color: "var(--text-tertiary)" }}>No events today</p> :
                todayEvents.map(ev => {
                  const et = EVENT_TYPES[ev.type] || EVENT_TYPES.sync;
                  return (
                    <div key={ev.id} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "0.5px solid var(--border)", cursor: ev.isDemo ? "pointer" : "default" }}
                      onClick={ev.isDemo ? () => navigate("/demo-bookings") : undefined}>
                      <div style={{ width: 3, borderRadius: 2, background: et.color, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>{ev.title}</div>
                        <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{ev.time}</div>
                        {ev.isDemo && <span style={{ fontSize: 9, color: "var(--purple)" }}>View in Demo Bookings &rarr;</span>}
                      </div>
                    </div>
                  );
                })
              }
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 8 }}>This Week</div>
              {weekEvents.length === 0 ? <p style={{ fontSize: 12, color: "var(--text-tertiary)" }}>No events this week</p> :
                weekEvents.map(ev => {
                  const et = EVENT_TYPES[ev.type] || EVENT_TYPES.sync;
                  return (
                    <div key={ev.id} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "0.5px solid var(--border)", cursor: ev.isDemo ? "pointer" : "default" }}
                      onClick={ev.isDemo ? () => navigate("/demo-bookings") : undefined}>
                      <div style={{ width: 3, borderRadius: 2, background: et.color, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 500 }}>{ev.title}</div>
                        <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>Day {ev.day} · {ev.time}</div>
                        {ev.isDemo && <span style={{ fontSize: 9, color: "var(--purple)" }}>View in Demo Bookings &rarr;</span>}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="ai-banner" style={{ padding: 12 }} data-testid="calendar-ai-banner">
              <div className="ai-pill"><Sparkles style={{ width: 10, height: 10 }} /> Claude AI</div>
              <p style={{ fontSize: 11, lineHeight: 1.5 }}>
                {demoCount} demo{demoCount !== 1 ? "s" : ""} scheduled this month.
                {rescheduleCount > 0 && <> {rescheduleCount} rescheduled — consider a <button onClick={() => navigate("/communications?tab=tasks")} style={{ background: "none", border: "none", color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontFamily: "var(--font)", fontSize: 11, padding: 0 }} data-testid="ai-tasks-link">qualification follow-up</button>.</>}
                {" "}<button onClick={() => navigate("/demo-bookings")} style={{ background: "none", border: "none", color: "var(--purple)", textDecoration: "underline", cursor: "pointer", fontFamily: "var(--font)", fontSize: 11, padding: 0 }} data-testid="ai-demo-link">View all demos</button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* New Event Modal */}
      {showNewEvent && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setShowNewEvent(false); setNewEventDate(null); }}>
          <div style={{ background: "#fff", borderRadius: 12, width: 400, border: "0.5px solid #eee" }} onClick={e => e.stopPropagation()} data-testid="new-event-modal">
            <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>New Event{newEventDate ? ` · ${monthNames[newEventDate.month]} ${newEventDate.day}` : ""}</span>
              <button onClick={() => { setShowNewEvent(false); setNewEventDate(null); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 16 }}>Choose what type of event to create:</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={() => { setShowNewEvent(false); navigate("/demo-bookings"); }} data-testid="new-event-demo"
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: "1px solid var(--border)", borderRadius: 8, background: "#fff", cursor: "pointer", textAlign: "left", fontFamily: "var(--font)" }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f3f0ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CalendarCheck style={{ width: 16, height: 16, color: "var(--purple)" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Schedule a Demo</div>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Opens Demo Bookings to schedule with full booking flow</div>
                  </div>
                </button>
                <button onClick={() => {
                    setShowNewEvent(false);
                    const params = new URLSearchParams({ tab: "tasks", new: "1" });
                    if (newEventDate) {
                      const mm = String(newEventDate.month + 1).padStart(2, "0");
                      const dd = String(newEventDate.day).padStart(2, "0");
                      params.set("due", `${newEventDate.year}-${mm}-${dd}`);
                    }
                    navigate(`/communications?${params.toString()}`);
                  }} data-testid="new-event-general"
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: "1px solid var(--border)", borderRadius: 8, background: "#fff", cursor: "pointer", textAlign: "left", fontFamily: "var(--font)" }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Calendar style={{ width: 16, height: 16, color: "var(--blue)" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>General Event / Task</div>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{newEventDate ? `Create a task or follow-up due ${monthNames[newEventDate.month]} ${newEventDate.day}` : "Create a follow-up task in Communications"}</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
